// AM Management · AM health — customers whose account manager disagrees between screens, plus the fix log.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Wrench } from "lucide-react";
import api from "../../../lib/api";
import { th, td, btn } from "./CustomersTab";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const v = (x) => x || <span className="text-slate-600">blank</span>;

export default function HealthTab() {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/am-health").then(({ data: d }) => { setData(d); setPicked(new Set()); }), []);
  useEffect(() => { load(); }, [load]);
  const fix = async (all) => {
    const n = all ? data.total : picked.size;
    if (!window.confirm(`Make the AM match on every screen for ${n} customer${n === 1 ? "" : "s"}? Each takes the AM last set in Talmia 2. This can't be undone, but every change is logged below.`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/am-health/fix", all ? { all: true } : { ids: [...picked] });
      toast.success(`Fixed ${r.fixed} customer${r.fixed === 1 ? "" : "s"}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fix failed"); } finally { setBusy(false); }
  };
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div className="space-y-4" data-testid="ams-health">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-300">
        <ShieldCheck className={`w-4 h-4 ${data.total ? "text-amber-300" : "text-emerald-300"}`} />
        <span data-testid="ams-health-count">{data.total ? `${data.total} customer${data.total === 1 ? " shows" : "s show"} a different AM on different screens` : "All customers show the same AM everywhere"}</span>
        <span className="text-slate-500">· the AM last set in Talmia 2 wins</span>
        {data.total > 0 && <div className="ml-auto flex gap-2">
          <button type="button" onClick={() => fix(false)} disabled={busy || !picked.size} className={`${btn} border-teal-500/40 text-teal-200`} data-testid="ams-health-fix-selected"><Wrench className="w-3.5 h-3.5" />Fix {picked.size} selected</button>
          <button type="button" onClick={() => fix(true)} disabled={busy} className={`${btn} border-emerald-500/40 bg-emerald-500/10 text-emerald-200`} data-testid="ams-health-fix-all">{busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Fix all</button>
        </div>}
      </div>
      {data.items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full"><thead><tr>
            <th className={th}><input type="checkbox" checked={picked.size === data.items.length} onChange={(e) => setPicked(e.target.checked ? new Set(data.items.map((i) => i.person_id)) : new Set())} data-testid="ams-health-pick-all" /></th>
            {["Customer", "Tier", "Talmia 2 AM", "Legacy field", "Linked user", "Will become", "Last change"].map((h) => <th key={h} className={th}>{h}</th>)}
          </tr></thead><tbody>
            {data.items.map((i) => (
              <tr key={i.person_id} className="border-t border-white/5" data-testid={`ams-health-row-${i.person_id}`}>
                <td className={td}><input type="checkbox" checked={picked.has(i.person_id)} onChange={() => toggle(i.person_id)} data-testid={`ams-health-pick-${i.person_id}`} /></td>
                <td className={td}><a href={`/crm/persons/${i.person_id}`} className="text-teal-300 hover:underline">{i.name}</a></td>
                <td className={td}>{i.tier || "—"}</td>
                <td className={td}>{v(i.primary)}</td><td className={td}>{v(i.legacy)}</td><td className={td}>{v(i.ref)}</td>
                <td className={`${td} text-emerald-200 font-semibold`}>{i.resolved || "Unassigned"}</td>
                <td className={td}>{i.last_change?.to ? `${i.last_change.to} · ${i.last_change.by || "?"} · ${when(i.last_change.ts)}` : "—"}</td>
              </tr>))}
          </tbody></table>
        </div>
      )}
      <div>
        <div className="text-[11px] uppercase tracking-widest font-mono text-slate-400 mb-2">Fix log ({data.log_total ?? data.log.length}{data.log_total > data.log.length ? `, latest ${data.log.length} shown` : ""})</div>
        {data.log.length === 0 ? <div className="text-[12px] text-slate-500">Nothing fixed yet.</div> : (
          <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-[360px] overflow-y-auto" data-testid="ams-health-log">
            {data.log.map((l, n) => (
              <div key={`${l.person_id}-${n}`} className="px-3 py-1.5 text-[11.5px] text-slate-300">
                <a href={`/crm/persons/${l.person_id}`} className="text-teal-300 hover:underline">{l.name || l.person_id}</a> → <b className="text-white">{l.after || "Unassigned"}</b>
                <span className="text-slate-500"> (was {[l.before?.primary, l.before?.legacy, l.before?.ref].map((x) => x || "blank").join(" / ")}) · {l.via} · {l.by} · {when(l.ts)}</span>
              </div>))}
          </div>)}
      </div>
    </div>
  );
}
